/*
 * Selector de almacén (Lima / Arequipa).
 *
 * La elección se guarda en el atributo de carrito "Almacen": Liquid lo lee en
 * cada página para filtrar productos y llega al pedido. localStorage es solo
 * respaldo para cuando el carrito se reinicia (ver snippets/almacen-head.liquid).
 *
 * Al cambiar de almacén se revisa el carrito contra el stock del almacén nuevo
 * (sections/almacen-cart-check.liquid); si algo no alcanza, se muestra antes de
 * ajustar. Luego se recarga para que la tienda se vuelva a renderizar filtrada.
 */
(() => {
  if (customElements.get('almacen-modal')) return;

  const CART_ATTRIBUTE = 'Almacen';
  const VALUES = { lima: 'Lima', arequipa: 'Arequipa' };
  const STORAGE_KEY = 'almacen';
  const DISMISSED_KEY = 'almacen_dismissed';
  const AUTO_OPEN_DELAY = 600;
  const RELOAD_DELAY = 900;
  const CLOSE_DURATION = 250;

  const root = document.documentElement;
  const rootUrl = window.Shopify?.routes?.root || '/';

  // El almacenamiento puede lanzar en modo privado o con cookies bloqueadas.
  const storage = {
    get(type, key) {
      try {
        return window[type].getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(type, key, value) {
      try {
        window[type].setItem(key, value);
      } catch (error) {}
    },
  };

  const updateCart = (body) =>
    fetch(`${rootUrl}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => {
      if (!response.ok) throw new Error(`cart/update.js respondió ${response.status}`);
      return response.json();
    });

  class AlmacenModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[role="dialog"]');
      this.steps = this.querySelectorAll('[data-step]');
      this.error = this.querySelector('[data-almacen-error]');
      this.strings = JSON.parse(this.querySelector('[data-almacen-strings]').textContent);
      this.pending = null;
      this.busy = false;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));
      document.addEventListener('click', this.onDocumentClick.bind(this));

      if (root.dataset.almacenSync === 'true') this.restoreCartAttribute();
      if (this.shouldAutoOpen()) setTimeout(() => this.autoOpen(), AUTO_OPEN_DELAY);
    }

    // El banner de cookies de Shopify va por encima de todo y debe poder
    // responderse: si está a la vista, el modal espera a que se responda.
    autoOpen() {
      const banner = document.getElementById('shopify-pc__banner');
      if (banner && banner.offsetHeight > 0) {
        document.addEventListener('visitorConsentCollected', () => setTimeout(() => this.open(), AUTO_OPEN_DELAY), {
          once: true,
        });
        return;
      }
      this.open();
    }

    get current() {
      return root.dataset.almacen || '';
    }

    shouldAutoOpen() {
      return !this.current && !window.Shopify?.designMode && !storage.get('sessionStorage', DISMISSED_KEY);
    }

    // Botones fuera del modal: [data-almacen-open] lo abre y
    // [data-almacen-choose] (p. ej. el aviso de la ficha) además elige directo.
    onDocumentClick(event) {
      if (this.contains(event.target)) return;
      const opener = event.target.closest('[data-almacen-open]');
      const chooser = event.target.closest('[data-almacen-choose]');
      if (!opener && !chooser) return;

      event.preventDefault();
      this.open(opener || chooser);
      if (chooser) this.choose(chooser.dataset.almacenChoose);
    }

    onClick(event) {
      if (event.target.closest('[data-almacen-close]')) return this.close();

      const chooser = event.target.closest('[data-almacen-choose]');
      if (chooser) return this.choose(chooser.dataset.almacenChoose, chooser);

      const confirm = event.target.closest('[data-almacen-confirm]');
      if (confirm && this.pending) return this.save(this.pending.key, this.pending.updates, confirm);
      if (event.target.closest('[data-almacen-back]')) return this.showStep('choose');
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusables = [...this.dialog.querySelectorAll('button:not([disabled]), [href]')].filter(
        (element) => element.offsetParent !== null
      );
      if (!focusables.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    open(opener) {
      if (!this.hidden) return;
      clearTimeout(this.closeTimer);

      this.opener = opener || document.activeElement;
      this.pending = null;
      this.toggleError(false);
      this.showStep('choose');
      this.hidden = false;
      root.classList.add('m-almacen-lock');

      // Forzar reflow para que la transición de entrada se vea.
      void this.offsetWidth;
      this.classList.add('is-open');
      this.dialog.focus({ preventScroll: true });
    }

    close() {
      if (this.hidden || this.busy) return;
      if (!this.current) storage.set('sessionStorage', DISMISSED_KEY, '1');

      this.classList.remove('is-open');
      root.classList.remove('m-almacen-lock');
      this.closeTimer = setTimeout(() => {
        this.hidden = true;
      }, CLOSE_DURATION);
      this.opener?.focus?.({ preventScroll: true });
    }

    showStep(name) {
      this.steps.forEach((step) => {
        step.hidden = step.dataset.step !== name;
      });
      this.dialog.dataset.step = name;
    }

    async choose(key, button) {
      if (!VALUES[key] || this.busy) return;
      if (key === this.current) return this.close();

      this.toggleError(false);
      this.setBusy(true, button || this.querySelector(`.m-almacen-option[data-almacen-choose="${key}"]`));

      let conflicts = [];
      try {
        conflicts = await this.getConflicts(key);
      } catch (error) {
        // La revisión es una ayuda: si falla, se cambia igual y el carrito
        // marcará las líneas sin stock (snippets/almacen-line-warning.liquid).
        console.error('[almacen] No se pudo revisar el carrito', error);
      }

      if (conflicts.length) {
        this.pending = { key, updates: this.buildUpdates(conflicts) };
        this.renderConflicts(key, conflicts);
        this.setBusy(false);
        this.showStep('conflict');
        this.querySelector('[data-almacen-confirm]').focus({ preventScroll: true });
        return;
      }

      await this.save(key);
    }

    async getConflicts(key) {
      const response = await fetch(`${rootUrl}?sections=almacen-cart-check`);
      if (!response.ok) throw new Error(`almacen-cart-check respondió ${response.status}`);

      const sections = await response.json();
      const html = new DOMParser().parseFromString(sections['almacen-cart-check'] || '', 'text/html');
      const items = JSON.parse(html.querySelector('[data-almacen-cart]')?.textContent || '[]');

      return items
        .map((item) => ({ ...item, available: Math.max(0, Math.floor(Number(item.stock[key]) || 0)) }))
        .filter((item) => item.available < item.quantity);
    }

    // Cantidad nueva por línea: lo disponible, o 0 (quitar) si no llega al mínimo.
    buildUpdates(conflicts) {
      return Object.fromEntries(
        conflicts.map((item) => [item.key, item.available >= item.min ? item.available : 0])
      );
    }

    renderConflicts(key, conflicts) {
      this.querySelector('[data-almacen-conflict-text]').textContent = this.strings.conflictText.replace(
        '__name__',
        this.strings.names[key]
      );

      const rows = conflicts.map((item) => {
        const quantity = this.pending.updates[item.key];
        const row = document.createElement('li');
        row.className = 'm-almacen-conflict';

        const media = document.createElement('span');
        media.className = 'm-almacen-conflict__media';
        if (item.image) {
          const image = new Image(56, 56);
          image.src = item.image;
          image.alt = '';
          image.loading = 'lazy';
          media.append(image);
        }

        const body = document.createElement('span');
        body.className = 'm-almacen-conflict__body';

        const title = document.createElement('span');
        title.className = 'm-almacen-conflict__title';
        title.textContent = item.title;
        body.append(title);

        if (item.variant) {
          const variant = document.createElement('span');
          variant.className = 'm-almacen-conflict__variant';
          variant.textContent = item.variant;
          body.append(variant);
        }

        const status = document.createElement('span');
        status.className = `m-almacen-conflict__status m-almacen-conflict__status--${quantity ? 'reduce' : 'remove'}`;
        status.textContent = quantity
          ? this.strings.conflictReduce.replace('__quantity__', quantity)
          : this.strings.conflictRemove;
        body.append(status);

        row.append(media, body);
        return row;
      });

      this.querySelector('[data-almacen-conflict-list]').replaceChildren(...rows);
    }

    async save(key, updates = {}, button) {
      if (this.busy && button) return;
      this.toggleError(false);
      this.setBusy(true, button || this.querySelector('.is-loading'));

      try {
        const body = { attributes: { [CART_ATTRIBUTE]: VALUES[key] } };
        if (Object.keys(updates).length) body.updates = updates;
        await updateCart(body);
      } catch (error) {
        console.error('[almacen] No se pudo guardar el almacén', error);
        this.setBusy(false);
        this.toggleError(true);
        return;
      }

      storage.set('localStorage', STORAGE_KEY, key);
      // El CSS filtra en el acto mientras se recarga.
      root.dataset.almacen = key;

      this.querySelector('[data-almacen-done-title]').textContent = this.strings.doneTitle.replace(
        '__name__',
        this.strings.names[key]
      );
      this.showStep('done');
      setTimeout(() => this.reload(), RELOAD_DELAY);
    }

    reload() {
      // La página N de una colección puede quedar vacía con el filtro nuevo.
      const url = new URL(window.location.href);
      url.searchParams.delete('page');
      if (url.href === window.location.href) {
        window.location.reload();
      } else {
        window.location.replace(url.href);
      }
    }

    restoreCartAttribute() {
      const key = this.current;
      if (!VALUES[key]) return;

      updateCart({ attributes: { [CART_ATTRIBUTE]: VALUES[key] } })
        .then(() => delete root.dataset.almacenSync)
        .catch((error) => console.error('[almacen] No se pudo restaurar el almacén en el carrito', error));
    }

    setBusy(busy, button) {
      this.busy = busy;
      this.classList.toggle('is-busy', busy);
      this.querySelectorAll('.is-loading').forEach((element) => element.classList.remove('is-loading'));
      if (busy && button) button.classList.add('is-loading');
    }

    toggleError(show) {
      if (this.error) this.error.hidden = !show;
    }
  }

  customElements.define('almacen-modal', AlmacenModal);
})();
